import styles from '../marketplace.module.css'

import {TextInput} from '@primer/react'
import useColorModes from '@github-ui/react-core/use-color-modes'
import {SearchIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {debounce} from '@github/mini-throttle'

export default function MarketplaceHeader() {
  const headerImages = ['arrow', 'actions', 'donut', 'semicircles', 'sparklesmall', 'sparklelarge', 'workflow']

  const colorMode = useColorModes()

  const colorModeAwareAssetDirectory = useMemo(() => {
    switch (colorMode.colorMode) {
      case 'night':
        return 'dark'
      case 'day':
      default:
        return 'light'
    }
  }, [colorMode])

  const [searchParams, setSearchParams] = useSearchParams()

  const queryWithoutSort = useMemo(() => {
    const queryString = searchParams.get('query')
    if (!queryString) {
      return ''
    }

    return queryString.replace(/sort:([^ ]*)/, '').trim()
  }, [searchParams])
  const [query, setQuery] = useState<string>(queryWithoutSort || '')

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetParams = useCallback(
    debounce((newQuery: string) => {
      const newParams = new URLSearchParams()
      newParams.set('query', newQuery)
      setSearchParams(newParams)
    }, 400),
    [searchParams],
  )

  const sortValue = useMemo(() => {
    const queryString = searchParams.get('query')
    if (!queryString) {
      return null
    }

    const match = queryString.match(/sort:([^ ]*)/)
    return match ? match[1] : null
  }, [searchParams])

  const onQueryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const queryWithSort = event.target.value + (sortValue ? ` sort:${sortValue}` : '')
      setQuery(event.target.value)
      debouncedSetParams(queryWithSort)
    },
    [debouncedSetParams, sortValue],
  )

  return (
    <header
      className={`mb-2 border-bottom borderColor-default d-flex flex-items-start py-5 py-lg-4 position-relative overflow-hidden ${styles['header']}`}
    >
      <div className={`${styles['gradient']} ${styles['gradient-left']}`} />
      <div className={`${styles['gradient']} ${styles['gradient-right']}`} />
      <div className="container-xl width-full m-auto d-flex flex-items-center flex-column flex-lg-row-reverse flex-justify-between gap-4 px-3 pl-lg-4 pr-lg-5 pl-xl-3">
        <div className={`position-relative mb-4 mb-lg-0 user-select-none ${styles['image-wrapper']}`}>
          {headerImages.map(image => (
            <img
              key={image}
              src={`/images/modules/marketplace/header/${colorModeAwareAssetDirectory}/${image}.png`}
              srcSet={`/images/modules/marketplace/header/${colorModeAwareAssetDirectory}/${image}@2x.png 2x`}
              className={`position-absolute ${styles[image]}`}
              alt=""
            />
          ))}
          <img
            src={`/images/modules/marketplace/header/${colorModeAwareAssetDirectory}/copilot.png`}
            srcSet={`/images/modules/marketplace/header/${colorModeAwareAssetDirectory}/copilot@2x.png 2x`}
            className={`m-lg-4 ${styles['copilot']}`}
            alt=""
          />
        </div>
        <div className={`text-center text-lg-left ${styles['header-content']}`}>
          <h1 className="lh-condensed text-wrap-balance">Enhance your workflow with extensions</h1>
          <p className="fgColor-muted f3">
            Tools from the community and partners to simplify tasks and automate processes
          </p>
          <TextInput
            block
            size="large"
            sx={{mt: 3}}
            leadingVisual={SearchIcon}
            aria-label="Search Marketplace"
            placeholder="Search for Copilot extensions, apps, and actions"
            className={`width-full ${styles['search-input']}`}
            value={query}
            onChange={onQueryChange}
          />
        </div>
      </div>
    </header>
  )
}

try{ MarketplaceHeader.displayName ||= 'MarketplaceHeader' } catch {}